import BaseController from "../sharedControllers/BaseController";
import {
  RemoveApiAccessToken,
  RemoveApiRefreshToken,
  SetApiAccessToken,
} from "akademika/akamont/services/AuthService";
import { Routes } from "akademika/akamont/constants/Routes";
import JSONModel from "sap/ui/model/json/JSONModel";
import { AppSettings } from "akademika/akamont/constants/AppSettings";
import { MenuData } from "../../models/MenuData";
import { TileData } from "akademika/akamont/models/TileData";
import {
  AllDashboardTiles,
  FilterTiles,
} from "akademika/akamont/utils/DashboardTileUtil";
import { UpdateLanguageFromAccessToken } from "akademika/akamont/utils/LanguageUtil";
import Toolbar from "sap/m/Toolbar";
import Text from "sap/m/Text";
import HBox from "sap/m/HBox";

/**
 * @namespace akademika.akamont.pages.app
 */
export default class App extends BaseController {
  private menuItems: MenuData[] = [
    this.getMenuItemFromTile(AllDashboardTiles.LogList),
    this.getMenuItemFromTile(AllDashboardTiles.BusinessUnits),
    this.getMenuItemFromTile(AllDashboardTiles.ContractsList),
    this.getMenuItemFromTile(AllDashboardTiles.MeasurementUnitList),
    this.getMenuItemFromTile(AllDashboardTiles.ServiceList),
    {
      text: "languages",
      icon: "sap-icon://world",
      roles: [],
      items: AppSettings.languages.map((lang) => {
        return {
          text: lang.name,
          press: this.changeLanguage,
          roles: [],
        } as MenuData;
      }),
    },
    {
      text: "logout",
      press: this.onLogout,
      icon: "sap-icon://log",
      roles: [],
    },
  ];

  public onInit() {
    // apply content density mode to root view
    const view = this.getView();
    if (view) {
      view.addStyleClass(this.getOwnerComponent().getContentDensityClass());
    }
    this.setModel(
      this.getOwnerComponent().globalLoadingModel,
      "globalLoadingModel"
    );
    // put data on this model instead of the default one, because all views share this view's models until they set their own
    this.setModel(new JSONModel(), "navbarMenuModel");

    this.getRouter().attachRouteMatched(async () => {
      const isUserAuthenticated =
        await this.getOwnerComponent().checkUserAuthenticationSynchronized();

      this.setModel(
        new JSONModel({ value: isUserAuthenticated }),
        "isUserAuthenticated"
      );
      if (isUserAuthenticated) {
        this.addMenuItems();
        this.SetMenuModelName();
      }
    });

    this.addMenuItems();

    this.updateHeaderForEnv();
  }

  private updateHeaderForEnv() {
    const toolbar = this.byId("headerToolbar") as Toolbar;
    if (!toolbar) return;

    const env = AppSettings?.env ?? "dev";
    const textId = "envText";
    let text = "";

    if (env === "test") {
      text = this.translate("headerTextTest");
      toolbar.addStyleClass("test-env-header");
    } else if (env === "dev") {
      text = this.translate("headerTextDev");
      toolbar.addStyleClass("dev-env-header");
    } else { }

    if (text && !toolbar.getContent()?.some(control => control.data("id") === textId)) {
      const oText = new Text({
        text,
        id: textId,
      });
      oText.addStyleClass("env-text sapUiSmallMargin")
      const oHbox = new HBox({
        items: [oText],
      });
      oHbox.addStyleClass("env-box");
      toolbar.insertContent(oHbox, 0)
    }
  }

  private addMenuItems() {
    let filteredMenuItems = FilterTiles(this.menuItems);

    this.getModel("navbarMenuModel").setProperty(
      "/menuItems",
      filteredMenuItems
    );
  }

  public navToDashboard(): void {
    this.navTo(Routes.DASHBOARD);
  }

  private onLogout(): void {
    RemoveApiAccessToken();
    RemoveApiRefreshToken();
    window.location.href = "/";
  }

  private async changeLanguage(menuData: MenuData) {
    const language = AppSettings.languages.find(
      (l) => l.name === menuData.text
    );

    if (language) {
      const newAccessToken = await this.post<string>(
        `api/User/UpdateLanguage/${language.code}`
      );

      // language is primarily read from the access token, so we need to update it
      SetApiAccessToken(newAccessToken);
      UpdateLanguageFromAccessToken(newAccessToken);
    }
  }

  private getMenuItemFromTile(tile: TileData): MenuData {
    return {
      text: tile.headerRaw,
      press: this.pressNavTo,
      target: tile.target,
      icon: tile.icon,
      roles: tile.roles,
    };
  }

  private pressNavTo(menuData: MenuData) {
    if (menuData.target) {
      this.navTo(menuData.target, menuData.targetParams);
    }
  }

  public onMenuItemPressed(menuData: MenuData) {
    if (menuData.press) menuData.press.apply(this, [menuData]);
  }
}
